import { useRequiredContext } from "@redotech/react-util/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { useInfiniteQuery } from "@tanstack/react-query";

const CUSTOM_ANALYTICS_REPORTS_PAGE_SIZE = 25;
const CUSTOM_ANALYTICS_REPORTS_QUERY_KEY = "customAnalyticsReports";

export interface CustomAnalyticsReportsFilters {
  createdByUserId?: string;
  createdAtStart?: Date;
  createdAtEnd?: Date;
}

export function getCustomAnalyticsReportsQueryInvalidationKey(teamId: string) {
  return [CUSTOM_ANALYTICS_REPORTS_QUERY_KEY, teamId] as const;
}

export function getCustomAnalyticsReportsQueryKey(
  teamId: string,
  filters?: CustomAnalyticsReportsFilters,
) {
  const createdByUserId = filters?.createdByUserId ?? null;
  const createdAtStart = filters?.createdAtStart?.toISOString() ?? null;
  const createdAtEnd = filters?.createdAtEnd?.toISOString() ?? null;

  return [
    ...getCustomAnalyticsReportsQueryInvalidationKey(teamId),
    { createdByUserId, createdAtStart, createdAtEnd },
  ] as const;
}

export function useCustomAnalyticsReportsQuery({
  enabled = true,
  filters,
}: { enabled?: boolean; filters?: CustomAnalyticsReportsFilters } = {}) {
  const team = useRequiredContext(TeamContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  return useInfiniteQuery({
    queryKey: getCustomAnalyticsReportsQueryKey(team._id, filters),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      return await rpcClient.listCustomAnalyticsReports({
        cursor: pageParam,
        limit: CUSTOM_ANALYTICS_REPORTS_PAGE_SIZE,
        createdByUserId: filters?.createdByUserId,
        createdAtStart: filters?.createdAtStart,
        createdAtEnd: filters?.createdAtEnd,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 10 * 1000, // 10 seconds
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    enabled,
  });
}
